// Shared upload panel — mounted in three places (FeeTable per-row invoice upload,
// §6 missing-invoice rollup, ReconcileSheet invoice-short) plus §6 checklist docs.
// Ported from the prototype's InvoiceUploadPanel (Documents.jsx:253-339), with the
// prototype's direct-file mock replaced by the real server round-trip: the file is
// POSTed to `doc-order-upload`, which extracts + AI-audits SERVER-SIDE and returns
// a verdict. Two modes:
//   • invoice — folder select (DO_INVOICE_FOLDERS), "AI reads the amount"; a filed
//     invoice appends to fee.invoices and clears the fee's requirement.
//   • doc — fixed eFolder container (DO_DOC_EFOLDER_MAP), "AI audits the document";
//     the §6 row lights from the mirror the queue writes after the real upload.
// PDF-only (accept=".pdf" — Codex amendment 1). Rendered INLINE — no body portal
// (the tab lives inside a Radix modal Sheet whose pointer-events lock inerts
// portals). Writes are gated by ctx.writebacks.enabled; when off, the commit is
// disabled with the reason surfaced inline + on the button tooltip.
import { useRef, useState, type ChangeEvent } from 'react';
import { Icon } from '../atoms';
import { useDocOrderCtx } from '../context';
import { DO_DOC_EFOLDER_MAP, DO_INVOICE_FOLDERS, suggestInvoiceFolder, type ChecklistDoc, type DocOrderFee } from '../core';
import type { DocOrderUploadTarget } from '@/services/docOrderService';

type UploadPanelProps = (
  | { mode: 'invoice'; fee: DocOrderFee; fees?: DocOrderFee[]; onClose: () => void }
  | { mode: 'doc'; doc: ChecklistDoc; onClose: () => void }
) & { disabled?: boolean; onPendingChange?: (pending: boolean) => void };

export function UploadPanel(props: UploadPanelProps) {
  const { writebacks, uploadFile, addToast } = useDocOrderCtx();
  const inputRef = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [fileName, setFileName] = useState('');
  const [pending, setPending] = useState(false);
  // Two distinct inline messages: a rejected AI verdict (prototype copy) vs a
  // transport/gate error (retryable Docling outage, disabled writes, …).
  const [rejectMsg, setRejectMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const invoiceMode = props.mode === 'invoice';
  const writesDisabled = !writebacks.enabled;
  const interactionDisabled = !!props.disabled;

  // Invoice mode: seed the folder from the fee's existing invoice bucket (so a
  // second upload for the same fee lands together), else the name-based guess.
  const invoiceDefaultFolder =
    props.mode === 'invoice'
      ? (props.fee.invoices ?? []).find((i) => i.folder)?.folder ?? suggestInvoiceFolder(props.fee.name, props.fee, props.fees)
      : '';
  const [folder, setFolder] = useState(invoiceDefaultFolder);
  // Doc mode: the eFolder container is fixed by the checklist id (created on the fly).
  const docFolder = props.mode === 'doc' ? DO_DOC_EFOLDER_MAP[props.doc.id] ?? props.doc.name : '';

  const onPick = (e: ChangeEvent<HTMLInputElement>) => {
    if (interactionDisabled) {
      return;
    }
    const picked = e.target.files?.[0] ?? null;
    if (picked) {
      setFile(picked);
      setFileName(picked.name);
      setRejectMsg(null);
      setErrorMsg(null);
    }
  };

  const commit = async () => {
    if (!file || pending || writesDisabled || interactionDisabled) {
      return;
    }
    const target: DocOrderUploadTarget =
      props.mode === 'invoice' ? { kind: 'invoice', feeId: props.fee.id, folder } : { kind: 'doc', checklistId: props.doc.id };
    setPending(true);
    props.onPendingChange?.(true);
    setRejectMsg(null);
    setErrorMsg(null);
    const res = await uploadFile(target, file, fileName).finally(() => {
      setPending(false);
      props.onPendingChange?.(false);
    });
    if (res.upload?.verdict === 'accepted') {
      // Filed ≠ accepted (round-25 P1): the entry is QUEUED — the row/dot
      // lights only when the file actually lands in the eFolder, so the toast
      // claims queuing, never delivery (convergence-copy rule).
      addToast(
        invoiceMode ? `Invoice accepted — uploading to Encompass · ${folder}` : `Audit passed — uploading to ${docFolder}`,
        'fileCheck'
      );
      props.onClose();
      return;
    }
    if (res.upload?.verdict === 'rejected') {
      setRejectMsg(
        invoiceMode
          ? `AI validation flagged “${fileName}” — ${res.upload.reason} Not filed; the invoice requirement stays open.`
          : `AI audit rejected “${fileName}” — ${res.upload.reason} Not filed to the eFolder.`
      );
      return;
    }
    // No verdict ⇒ the upload never reached the audit gate.
    if (res.code === 'extraction_unavailable') {
      setErrorMsg('Document could not be read — try again.');
    } else if (res.code === 'writeback_disabled') {
      setErrorMsg('Encompass writes are disabled for this loan.');
    } else if (res.code === 'revision_conflict') {
      // runAction already reloaded + toasted — just dismiss the now-stale panel.
      props.onClose();
    } else if (!res.ok) {
      setErrorMsg(res.error || 'Upload failed — try again.');
    }
  };

  const commitDisabled = !fileName || pending || writesDisabled || interactionDisabled;

  return (
    <div className="im-upload-panel">
      <label className="im-up-field">
        <span className="im-up-lbl">Encompass folder</span>
        {invoiceMode ? (
          <select className="im-up-select" value={folder} disabled={pending || interactionDisabled} onChange={(e) => setFolder(e.target.value)}>
            {DO_INVOICE_FOLDERS.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        ) : (
          <span className="im-up-folder-fixed" title={`Files into the ${docFolder} eFolder container`}>
            <Icon name="folder" size={11} />
            {docFolder}
          </span>
        )}
      </label>
      <label className="im-up-field im-up-file">
        <span className="im-up-lbl">{invoiceMode ? 'File · AI reads the amount' : 'File · AI audits the document'}</span>
        <button type="button" className="im-up-pick" disabled={pending || interactionDisabled} onClick={() => inputRef.current?.click()}>
          <Icon name="fileText" size={11} />
          {fileName || 'Choose PDF…'}
        </button>
        <input ref={inputRef} type="file" accept=".pdf" hidden disabled={pending || interactionDisabled} onChange={onPick} />
      </label>
      {rejectMsg && (
        <div className="doc-audit-fail" style={{ gridColumn: '1 / -1' }}>
          <Icon name="alertTriangle" size={10} />
          {rejectMsg}
        </div>
      )}
      {errorMsg && (
        <div className="doc-audit-fail" style={{ gridColumn: '1 / -1' }}>
          <Icon name="alertTriangle" size={10} />
          {errorMsg}
        </div>
      )}
      {writesDisabled && (
        <div className="im-up-disabled-note" style={{ gridColumn: '1 / -1' }}>
          <Icon name="alertCircle" size={10} />
          Encompass writes are disabled for this loan — uploads are unavailable until it’s enabled.
        </div>
      )}
      <div className="im-up-actions">
        <button type="button" className="im-up-cancel" disabled={pending || interactionDisabled} onClick={props.onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={`im-up-commit ${pending ? 'is-busy' : ''}`.trim()}
          disabled={commitDisabled}
          aria-busy={pending}
          // R4 #23: the global `button:disabled:not([data-preserve-disabled])
          // { background-color: transparent !important }` rule (src/index.css)
          // blanked this button to a white void the moment the upload disabled
          // it — the attribute is that rule's designed escape hatch.
          data-preserve-disabled
          title={writesDisabled ? 'Encompass writes are disabled for this loan' : undefined}
          onClick={() => void commit()}
        >
          {/* #16: the multipart round-trip is several seconds — show an explicit
              spinner and KEEP the label so the button never reads as blank/hung. */}
          <Icon name={pending ? 'refresh' : 'upload'} size={11} />
          {pending ? 'Uploading to Encompass…' : 'Upload to Encompass'}
        </button>
      </div>
    </div>
  );
}
